import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { resolveAssetUrl } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/sobre", label: "Sobre Nós" },
  { to: "/servicos", label: "Serviços" },
  { to: "/orcamento", label: "Orçamento" },
  { to: "/visita-tecnica", label: "Visita Técnica" },
  { to: "/contato", label: "Contato" },
];

const DARK_HERO_ROUTES = ["/", "/sobre", "/servicos"];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const { content } = useSite();
  const lightLogo = resolveAssetUrl(content?.logo_url_light, "/brand/logo-light.png");
  const whiteLogo = resolveAssetUrl(content?.logo_url_white, "/brand/logo-white.png");

  const hasDarkHero = DARK_HERO_ROUTES.includes(loc.pathname);
  const onDark = !scrolled && hasDarkHero;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        onDark ? "bg-transparent" : "bg-white/85 backdrop-blur-xl shadow-sm border-b border-slate-200/60"
      }`}
    >
      <div className="dc-container flex items-center justify-between h-20">
        <Link to="/" data-testid="header-logo" className="flex items-center group">
          <img
            src={onDark ? whiteLogo : lightLogo}
            alt="DeskcodeBR"
            className="h-11 md:h-12 w-auto group-hover:scale-105 transition-transform"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.to.replace("/", "") || "home"}`}
              className={({ isActive }) => {
                const base = "px-4 py-2 rounded-full text-sm font-medium transition-all";
                if (onDark) {
                  return `${base} ${isActive ? "text-white bg-white/15" : "text-white/85 hover:text-white hover:bg-white/10"}`;
                }
                return `${base} ${isActive ? "text-dc-primary bg-dc-primary/8" : "text-slate-700 hover:text-dc-primary hover:bg-slate-50"}`;
              }}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center">
          <Link
            to="/orcamento"
            data-testid="header-cta-orcamento"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all hover:-translate-y-0.5 ${
              onDark
                ? "bg-dc-accent hover:bg-dc-accent-hover text-white shadow-lg shadow-dc-accent/30"
                : "bg-dc-primary hover:bg-dc-primary-hover text-white shadow-lg shadow-dc-primary/25"
            }`}
          >
            Solicitar orçamento <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <button
          data-testid="header-menu-toggle"
          className={`lg:hidden p-2 rounded-lg transition ${onDark ? "text-white hover:bg-white/10" : "hover:bg-slate-100 text-slate-700"}`}
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="dc-container py-4 flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `py-3 px-2 border-b border-slate-100 text-sm font-medium ${isActive ? "text-dc-primary" : "text-slate-700"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/orcamento"
              data-testid="mobile-cta-orcamento"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-dc-primary text-white px-5 py-3 rounded-full font-semibold text-sm"
            >
              Solicitar orçamento <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
